// Trip Sheet — template + review checklist. Strictly factual; never declares
// the well safe or certifies flow-check results.
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);
const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Helper exposed for the page to display a live "diff" in trip-tank volumes.
export function tripTankDiff(state) {
  const s = safeNum(state.tank?.startVol);
  const e = safeNum(state.tank?.endVol);
  if (s === null || e === null) return null;
  return Math.round((e - s) * 100) / 100;
}

export function fillVsExpectedDiff(state) {
  const exp = safeNum(state.fill?.expected);
  const act = safeNum(state.fill?.actual);
  if (exp === null || act === null) return null;
  return Math.round((act - exp) * 100) / 100;
}

export function buildTripReport(state) {
  const h = state.header || {};
  const t = state.tripSummary || {};
  const tank = state.tank || {};
  const fill = state.fill || {};
  const flow = state.flow || {};

  const lines = [];
  const push = (s) => { if (s !== null && s !== undefined) lines.push(s); };
  const sec = (label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

  sec("Report Header");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}`);

  sec("Trip Summary");
  push(`Trip Direction: ${orMissing(t.direction)}`);
  push(`Start Depth: ${orMissing(t.startDepth)}  ·  End Depth: ${orMissing(t.endDepth)}`);
  push(`Pipe Size / Type: ${orMissing(t.pipe)}`);
  push(`Stand Count: ${orMissing(t.standCount)}`);

  sec("Trip Tank / Volume Tracking");
  push(`Trip Tank Start Volume: ${orMissing(tank.startVol)}`);
  push(`Trip Tank End Volume: ${orMissing(tank.endVol)}`);
  const diff = tripTankDiff(state);
  if (diff !== null) push(`Calculated Volume Change: ${diff}`);
  else push(`Calculated Volume Change: ${MISSING}`);

  sec("Fill-Up / Displacement Record");
  push(`Expected Fill / Displacement: ${orMissing(fill.expected)}`);
  push(`Actual Fill / Displacement: ${orMissing(fill.actual)}`);
  const fdiff = fillVsExpectedDiff(state);
  if (fdiff !== null) push(`Difference (Actual − Expected): ${fdiff}`);

  sec("Flow Checks");
  push(`Flow Check Performed: ${flow.performed ? "Yes" : "No"}`);
  const fr = polishProse(flow.result);
  push(`Flow Check Result (as recorded): ${fr || MISSING}`);
  push("Flow check observations recorded as-entered. Verification under approved procedures by competent personnel is required.");

  sec("Hole Fill / Overpull / Drag Notes");
  push(polishProse(state.overpullDrag?.notes) || MISSING);

  sec("Abnormal Observations");
  const sw = polishProse(state.observations?.swabSurge);
  if (sw) push(`Swab / Surge Concerns: ${sw}`);
  const lg = polishProse(state.observations?.lossGain);
  if (lg) push(`Loss / Gain Observations: ${lg}`);
  const other = polishProse(state.observations?.other);
  if (other) push(`Other Observations: ${other}`);
  if (!sw && !lg && !other) push(MISSING);

  sec("Mud Engineer / Rig Notes");
  push(polishProse(state.notes?.roughNotes) || MISSING);

  sec("Supervisor Review");
  push(polishProse(state.review?.notes) || MISSING);
  push("Supervisor signature required outside this tool.");

  return lines.join("\n");
}

export function tripReviewChecklist(state) {
  const out = [];
  const h = state.header || {};
  const t = state.tripSummary || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.preparedBy) out.push("Prepared-By missing");
  if (!t.direction) out.push("Trip direction missing");
  if (!t.startDepth) out.push("Start depth missing");
  if (!t.endDepth) out.push("End depth missing");
  const flow = state.flow || {};
  if (flow.performed === undefined) out.push("Flow-check status not recorded");
  return out;
}

export const TRIP_QUICK_PHRASES = [
  "No abnormal observations during trip.",
  "Trip tank levels within tolerance.",
  "Flow check performed, observations recorded.",
  "No overpull or drag noted.",
  "Hole fill matches expected displacement.",
  "Awaiting supervisor review.",
];
